// components/TagSearchFilter/index.js - VERSÃO CORRIGIDA
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Typography,
  Collapse,
  Badge,
  Tooltip,
  Paper,
  Fade
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FilterListIcon from '@mui/icons-material/FilterList';
import { matchesConversationText, normalizeSearchText } from '../../../../utils/conversation-search';
import './styles.css';

const noop = () => {};

const TagSearchFilter = ({ 
  chats = [], 
  onFilteredChatsChange,
  onSearchTermChange = noop,
  placeholder = "Buscar por tags...",
  showTagSuggestions = true 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Extrai todas as tags únicas dos chats
  const allUniqueTags = useMemo(() => {
    const tagMap = new Map();
    
    chats.forEach(chat => {
      if (chat.chattags && Array.isArray(chat.chattags)) {
        chat.chattags.forEach(tagItem => {
          if (tagItem.tag && tagItem.tag.name) {
            tagMap.set(tagItem.tag.id, {
              id: tagItem.tag.id,
              name: tagItem.tag.name
            });
          }
        });
      }
    });
    
    return Array.from(tagMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [chats]);

  // Tags sugeridas baseadas no termo de busca
  const suggestedTags = useMemo(() => {
    if (!searchTerm.trim() || !showTagSuggestions) return [];
    
    const term = normalizeSearchText(searchTerm);
    return allUniqueTags
      .filter(tag => 
        normalizeSearchText(tag.name).includes(term) &&
        !selectedTags.some(selected => selected.id === tag.id)
      )
      .slice(0, 5);
  }, [searchTerm, allUniqueTags, selectedTags, showTagSuggestions]);

  // CORREÇÃO: Filtra os chats baseado nas tags selecionadas E no termo de busca
  const filterChats = useCallback(() => {
    let filtered = chats;

    // Filtra por tags selecionadas
    if (selectedTags.length > 0) {
      filtered = filtered.filter(chat => {
        if (!chat.chattags || !Array.isArray(chat.chattags)) return false;
        
        return selectedTags.every(selectedTag =>
          chat.chattags.some(chatTag => 
            chatTag.tag && chatTag.tag.id === selectedTag.id
          )
        );
      });
    }

    // CORREÇÃO: Filtra também pelo termo de busca em tempo real
    if (searchTerm.trim()) {
      filtered = filtered.filter(chat => matchesConversationText(chat, searchTerm));
    }

    onFilteredChatsChange(filtered, selectedTags.length > 0 || Boolean(searchTerm.trim()));
  }, [chats, selectedTags, searchTerm, onFilteredChatsChange]);

  // Adiciona tag à seleção
  const handleAddTag = useCallback((tag) => {
    setSelectedTags(prev => {
      const newTags = [...prev, tag];
      return newTags;
    });
    setSearchTerm('');
    onSearchTermChange('');
  }, [onSearchTermChange]);

  // Remove tag da seleção
  const handleRemoveTag = useCallback((tagId) => {
    setSelectedTags(prev => prev.filter(tag => tag.id !== tagId));
  }, []);

  // Busca por tag ao clicar na lupa ou pressionar Enter
  const handleSearch = useCallback(() => {
    if (!searchTerm.trim()) return;

    const foundTag = allUniqueTags.find(tag => 
      normalizeSearchText(tag.name) === normalizeSearchText(searchTerm)
    );

    if (foundTag && !selectedTags.some(tag => tag.id === foundTag.id)) {
      handleAddTag(foundTag);
    }
  }, [searchTerm, allUniqueTags, selectedTags, handleAddTag]);

  // Limpa todos os filtros
  const handleClearAll = useCallback(() => {
    setSelectedTags([]);
    setSearchTerm('');
    onSearchTermChange('');
  }, [onSearchTermChange]);

  // CORREÇÃO: Atualiza filtro quando tags selecionadas OU termo de busca mudam
  useEffect(() => {
    filterChats();
  }, [filterChats]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // CORREÇÃO: Adiciona handler para mudança do campo de busca
  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearchTermChange(value);
  };

  return (
    <Box className="tag-search-filter-container">
      <Paper elevation={2} className="search-paper">
        <Stack spacing={2}>
          {/* Campo de busca */}
          <TextField
            fullWidth
            size="small"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchTermChange} // CORREÇÃO: Usando o handler correto
            onKeyPress={handleKeyPress}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Badge 
                    badgeContent={selectedTags.length} 
                    color="primary"
                    invisible={selectedTags.length === 0}
                  >
                    <FilterListIcon color="action" fontSize="small" />
                  </Badge>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {(searchTerm || selectedTags.length > 0) && (
                    <IconButton
                      size="small"
                      onClick={handleClearAll}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    edge="end"
                    color="primary"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingRight: 0.5,
              }
            }}
          />

          {/* Sugestões de tags */}
          <Collapse in={isSearchFocused && suggestedTags.length > 0}>
            <Paper variant="outlined" className="suggestions-paper">
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Sugestões:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {suggestedTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    icon={<LocalOfferIcon />}
                    onClick={() => handleAddTag(tag)}
                    className="suggestion-chip"
                  />
                ))}
              </Stack>
            </Paper>
          </Collapse>

          {/* Tags selecionadas */}
          <Fade in={selectedTags.length > 0}>
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Typography variant="caption" color="textSecondary">
                  Filtrando por:
                </Typography>
                {selectedTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    icon={<LocalOfferIcon />}
                    onDelete={() => handleRemoveTag(tag.id)}
                    color="primary"
                    className="selected-tag-chip"
                  />
                ))}
                {selectedTags.length > 1 && (
                  <Tooltip title="Limpar todos os filtros">
                    <IconButton size="small" onClick={handleClearAll}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Fade>

          {/* Contador de resultados - CORREÇÃO: Mostra também quando há termo de busca */}
          {(selectedTags.length > 0 || searchTerm.trim()) && (
            <Typography variant="caption" color="textSecondary" className="result-counter">
              {selectedTags.length > 0 
                ? `Mostrando conversas com ${selectedTags.length === 1 ? 'a tag' : 'todas as tags'} selecionada${selectedTags.length > 1 ? 's' : ''}` 
                : searchTerm.trim() 
                  ? `Filtrando por: "${searchTerm}"`
                  : ''
              }
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default TagSearchFilter;
